import icons from 'components/darwin-ui/Icon/Icons';
import { Product } from 'types/productsTypes';
import { DarwinListView } from 'components/darwin-ui';

import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { getProductColumns } from './columns';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';
import useTableControls from 'hooks/useTableControls';
import { generatePath, useNavigate } from 'react-router-dom';

import { ROUTES } from 'routes/routes';
import { useEffect, useState, useCallback } from 'react';
import { useGetProductsByCompany } from 'api/services/productsService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { DeleteProductModal } from 'components/ui/products/DeleteProductModal';
import { useUI } from 'contexts/UiContext';
import { ActivateDeactivateProduct } from 'components/ui/products/ActivateOrDeactivateProductModal';

export type ColumnAction = {
  key: string;
  name?: keyof typeof icons;
  label?: string;
  onClick?: (product: Product) => void;
};

export const Products = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();
  const [productToDelete, setProductToDelete] = useState<Product>();
  const [productToActiveDeactive, setProductToActiveDeactive] =
    useState<Product>();

  const checkPermission = usePermissionChecker();

  const viewProductPermission = checkPermission(
    PermissionModules.Products,
    PermissionActions.Read
  );
  const updateProductPermission = checkPermission(
    PermissionModules.Products,
    PermissionActions.Update
  );
  const deleteProductPermission = checkPermission(
    PermissionModules.Products,
    PermissionActions.Delete
  );
  const createProductPermission = checkPermission(
    PermissionModules.Products,
    PermissionActions.Create
  );

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls();

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
    isPending: isPendingProducts,
    error: productsError,
  } = useGetProductsByCompany({
    companyId: selectedCompany || '',
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
  });

  useEffect(() => {
    if (productsError?.message) {
      openNotification('Error', t('product.product-error-message'), 'error');
    }
  }, [openNotification, productsError?.message, t]);

  const isLoading =
    isLoadingProducts ||
    isFetchingProducts ||
    isPendingProducts ||
    viewProductPermission === null;

  const tableData = productsData?.data ?? [];

  const availableActions: Array<{
    permission: boolean | null;
    action: ColumnAction;
  }> = [
    {
      permission: updateProductPermission,
      action: {
        key: 'edit',
        name: 'edit',
        label: t('list-view.action-items.edit'),
        onClick: product =>
          navigate(
            generatePath(ROUTES.PRODUCTS.EDIT, {
              id: product.id.toString(),
            })
          ),
      },
    },
    {
      permission: viewProductPermission,
      action: {
        key: 'view',
        name: 'view',
        label: t('list-view.action-items.view'),
        onClick: product =>
          navigate(
            generatePath(ROUTES.PRODUCTS.VIEW, {
              id: product.id.toString(),
            })
          ),
      },
    },
    {
      permission: deleteProductPermission,
      action: {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: setProductToDelete,
      },
    },
    {
      permission: updateProductPermission,
      action: {
        key: 'activate-deactivate',
        onClick: setProductToActiveDeactive,
      },
    },
  ];

  const actions: ColumnAction[] = availableActions
    .filter(item => Boolean(item.permission))
    .map(item => item.action);

  const columns = getProductColumns(t, actions, searchQuery);

  const createNewProduct = () => {
    navigate(generatePath(ROUTES.PRODUCTS.CREATE));
  };

  const handleRowClick = useCallback(
    (product: Product) => {
      if (viewProductPermission) {
        navigate(
          generatePath(ROUTES.PRODUCTS.VIEW, {
            id: product.id.toString(),
          })
        );
      }
    },
    [viewProductPermission, navigate]
  );

  const handleCancel = useCallback(() => {
    setProductToActiveDeactive(undefined);
  }, [setProductToActiveDeactive]);

  return (
    <>
      <ContentHeader
        title={t('product.product-title')}
        {...(createProductPermission && {
          onSave: createNewProduct,
          onSaveTitle: t('product.add-new-product'),
        })}
      />

      <DarwinListView<Product>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          total: productsData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
        header={{
          setSearchQuery,
          searchPlaceholder: t('product.product-search-placeholder'),
        }}
        table={{
          onChange: onTableChange,
        }}
        behavior={{
          isRowInactive: product => !product.active,
          onRowClick: handleRowClick,
        }}
      />

      <DeleteProductModal
        open={!!productToDelete}
        onCancel={() => setProductToDelete(undefined)}
        productId={productToDelete?.id.toString()}
      />

      <ActivateDeactivateProduct
        open={!!productToActiveDeactive}
        onCancel={handleCancel}
        product={productToActiveDeactive}
        selectedCompany={selectedCompany || ''}
      />
    </>
  );
};
